import React from "react";
import axios, { AxiosResponse } from "axios";
import { AlertColor, Button, MenuItem, TextField, useMediaQuery} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import { DataGridPro, GridColumns, GridRowId, GridSelectionModel, GridToolbarContainer, GridValueSetterParams } from '@mui/x-data-grid-pro';
import Title from "../../component/Title";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { Bank, BankAccount } from "./BankAccessories";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { CustomTablePagination } from "../../component/CustomPagination";
import { BankSingle, BankSingleHandle } from "./BankSingle";
import { OpenMode } from "../../../common/Enums";

export function BankAccountList(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

	const singleModalRef = React.useRef<BankSingleHandle>(null);

    const style = { ...globalTheme() };
     
    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

	const hasChanges = React.useRef<boolean>(false);

    const [searchData, setSearchData] = React.useState<any>({
        pageSize: 10,
    });

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

	const [gridData, setGridData] = React.useState<BankAccount[]>([]);
	const [banks, setBanks] = React.useState<Bank[]>([]);
	const [bankAccounts, setBankAccounts] = React.useState<BankAccount[]>([]);
	const [selectedBank, setSelectedBank] = React.useState<Bank>();
	const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>(() =>
		gridData.map((r) => r.bankAccountID),
	);

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		hasChanges.current = false;

        axios.all([
			axios.get<any[]>(`Api/V1/Bank`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			}),
			axios.get<BankAccount[]>(`Api/V1/Bank/BankAccount/0`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			})
		  ]).then(axios.spread((...responses) => {
			const bankResult: AxiosResponse<Bank[]> = responses[0];
			const bankAccountResult: AxiosResponse<BankAccount[]> = responses[1];

			setBanks(bankResult.data);
			setSelectedBank(bankResult.data.filter(x => x.bankID == 1)[0]);

			setBankAccounts(bankAccountResult.data);
			let bankAccountsByBank = bankAccountResult.data.filter(x => x.bankID == 1);
	
			setGridData(bankAccountsByBank)
  
			setLoading(false);
		  })).catch((error) => {
			console.log(error);
			setBanks([]);
			setBankAccounts([]);
			setHasError(true);
			setLoading(false);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		  })
	}

	//Grid columns
	const columns: GridColumns = [
		{
			field: 'bankAccountNumber', headerName: 'Bank Account Number', flex: 1, type: 'string', editable: true,
			valueSetter: (param: GridValueSetterParams) => onCellValueChanged(param, "bankAccountNumber", 50)
		}
	];

    function onCellValueChanged(param: GridValueSetterParams, field: string, maxLength: number) {
		let row: BankAccount = param.row;
		Object.entries(row).forEach(([key, value]) => {
		  if (`${key}` === field)
		  {
			if (maxLength > 0)
			  Reflect.set(row, key, param.value.slice(0,maxLength));
			else
			  Reflect.set(row, key, param.value);
		  }
		});
  
		let tmpData = gridData;
		let index = tmpData.findIndex(x => x.bankAccountID === row.bankAccountID);
		Object.entries(tmpData[index]).forEach(([key, value]) => {
		  if (`${key}` === `${field}`)
		  {
			if (maxLength > 0)
			  Reflect.set(tmpData[index], key, param.value.slice(0,maxLength));
			else
			  Reflect.set(tmpData[index], key, param.value);
		  }
		});
		EditBankAccount(tmpData[index]);
  
		hasChanges.current = true;
		return { ...param.row, row };
	}
  
	function OnBankChanged(bankName: string)
	{
		setSelectedBank(GetBankByName(bankName));
		setGridData(bankAccounts.filter(x => x.bankID == getBankId(bankName)));
	}

	function OnSelectionChanged(model: GridSelectionModel) {
        const ids = new Set(selectionModel);
        if (model.length > 1) {
          setSelectionModel(model.filter((s) => !ids.has(s)));
        } 
        else {
          setSelectionModel(model);
        }
	}

	function CustomToolbar() {
		const [inputText, setInputText] = React.useState("");
		return (
			<GridToolbarContainer style={{ display: 'flex' }}>
				<TextField 
					label="New Bank Account"
					value = {inputText}
					style={{width: `25%`, minWidth: 400}}
					size="small"
					onChange={(e) => {
						setInputText(e.target.value);
					}}
				/>
				<Button variant="contained" size="small" color="secondary" style={{ margin: "2px" }} startIcon={<Add />} onClick={() => NewBankAccount(inputText)}>Add</Button>
				<div style={{ flexGrow: 1 }}></div>
			</GridToolbarContainer>
		);
	}

	function ReloadBanks(bank: Bank)
	{
		let bankList = [...banks];
		if (banks.find(x => x.bankID == bank.bankID))
			bankList[banks.findIndex(x => x.bankID == bank.bankID)] = bank;
		else
			bankList.push(bank);

		setBanks(bankList);
		setSelectedBank(bank);

		let bankAccountList = bankAccounts.filter(x => x.bankID == bank.bankID);
		setGridData(bankAccountList);	
	}

    function GetBankByName(bankName: string) {
		return banks.find(x => x.bankName == bankName);
	}

    function getBankId(bankName: string) {
		if (bankName == "")
		  return 0;
  
		return banks.find(x => x.bankName == bankName)?.bankID ?? 1;
	}

	function NewBank() {
		let bank: Bank = {
			bankID: 0,
			bankName: "",
			bankAddress: "",
			phone: "",
			fax: "",
			region: ""
		};
		singleModalRef.current?.Initialise(OpenMode.NEW, bank);
	}

	function EditBank() {
		if (selectedBank)
			singleModalRef.current?.Initialise(OpenMode.EDIT, selectedBank);
	}

	function NewBankAccount(accountNumber: string) {
		if (selectedBank)
		{
			if (accountNumber && accountNumber.length>0)
			{
				let acc: BankAccount = {
					bankID: selectedBank.bankID,
					bankAccountNumber: accountNumber,
					bankAccountID: 0
				};
				axios.post<BankAccount>('Api/V1/Bank/BankAccount', acc).then(
                    (result) => {
						let temp = [...gridData];
						temp.push(result.data);
						setGridData(temp);
                        showSnackBar("Bank account created successfully.", "", "success");
                    },
                    (error) => {
                        showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                    }
                );
			}
			else
			{
				showSnackBar("Bank account number is blank!", "Bank account number is not entered", "error");
			}
		}
		else
		{
			showSnackBar("You need to select a bank first!", "Bank is not selected", "error");
		}
	}

	function EditBankAccount(acc: BankAccount) {
		axios.put<BankAccount>('Api/V1/Bank/BankAccount', acc).then(
			(result) => {
				let temp = [...gridData];
				let index = temp.findIndex(x => x.bankAccountID === acc.bankAccountID);
				temp[index] = acc;
				setGridData(temp);
				showSnackBar("Bank account updated successfully.", "", "success");
			},
			(error) => {
				showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
			}
		);
	}

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Bank Accounts" />
			<div className={style.PageContent}>
				<div className={style.ModalFields}>
					<TextField className={style.ModalContentFieldThreeQuarterWidth}
						id="bank"
						style={{ minWidth: tinyScreen ? "274px" : (smallScreen ? "374px" : "494px") }}
						value={selectedBank?.bankName ?? ""}
						onChange={(e) => OnBankChanged(e.target.value)}
						select
						label="Bank Name"
						size="small"
						fullWidth
					>
						{banks.map((item) => (
						<MenuItem key={item.bankID} value={item.bankName}>
							{item.bankName}
						</MenuItem>
						))}
					</TextField>
					<Button variant="contained" size="small" color="secondary" style={{ margin: "4px" }} startIcon={<Add />} onClick={() => NewBank()}>Add</Button>
					<Button variant="contained" size="small" color="secondary" style={{ margin: "4px" }} startIcon={<Edit />} onClick={() => EditBank()}>Edit</Button>
				</div>
				<div className={style.ModalFields} style={{ height: "100%", minHeight: "200px" }}>
					<DataGridPro
						className={style.ModalContentFieldFullWidth}
						getRowId={(row) => row.bankAccountID}
						rows={gridData}
						columns={columns}
						pageSize={searchData.pageSize}
						onPageSizeChange={(pageSize) => setSearchData({ ...searchData, pageSize: pageSize })}
						pagination
						rowsPerPageOptions={[10, 30, 50, 100]}
						loading={loading}
						components={{
							Toolbar: CustomToolbar,
							Footer: CustomTablePagination
						}}
						componentsProps={{
							footer: { rowsPerPageOptions: [10, 30, 50, 100] }
						}}
						selectionModel={selectionModel}
						onSelectionModelChange={(selectionModel) => {
							OnSelectionChanged(selectionModel)
						}}
						rowHeight={tinyScreen ? 20 : (smallScreen ? 24 : 28)}
					/>
				</div>
			</div>

			<BankSingle
				ref={singleModalRef}
				onClose={(saved: boolean, bank: Bank) => {
					if (saved && bank.bankID > 0)
						ReloadBanks(bank);
				} } 
				customers={[]} 
				endUsers={[]} 
				regions={props.regions} 
				sales={[]} 
				deliveryMethods={[]} 
				paymentTerms={[]} 
				currencies={[]}			
			/> 

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

